// Open.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import logo from '../assets/Navbar/logo.png';
import beachResortImg from '../assets/Resort/119.jpeg';
import riverResortImg from '../assets/River/17.jpeg';

const Open = () => {
    const navigate = useNavigate();
    const [showLogo, setShowLogo] = useState(true);
    const [showCards, setShowCards] = useState(false);

    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 1200], [0, -180]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowLogo(false);
            setShowCards(true);
        }, 2200);

        return () => clearTimeout(timer);
    }, []);

    const resorts = [
        {
            id: 1,
            name: 'Muthumuni Ayurveda\nBeach Resort',
            image: beachResortImg,
            path: '/beach/home',
            description: 'Where ocean whispers meet ancient healing',
            tag: 'Oceanfront Sanctuary',

            cta: 'Discover Beach Haven',
        },
        {
            id: 2,
            name: 'Muthumuni Ayurveda\nRiver Resort',
            image: riverResortImg,
            path: '/river/home',
            description: 'Serenity flows with the river’s rhythm',
            tag: 'Riverside Retreat',

            cta: 'Enter River Sanctuary',
        },
    ];

    // ─── Animations ────────────────────────────────────────
    const logoVariants = {
        hidden: { opacity: 0, scale: 0.6, filter: 'blur(8px)' },
        visible: {
            opacity: 1,
            scale: 1,
            filter: 'blur(0px)',
            transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
        },
        exit: {
            opacity: 0,
            scale: 0.8,
            filter: 'blur(12px)',
            transition: { duration: 0.7, ease: 'easeIn' },
        },
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.24, delayChildren: 0.4 },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 80, scale: 0.94 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { duration: 1, ease: [0.22, 1, 0.36, 1] },
        },
        hover: {
            y: -16,
            scale: 1.03,
            transition: { duration: 0.4, ease: 'easeOut' },
        },
    };

    const imageVariants = {
        hover: {
            scale: 1.09,
            transition: { duration: 0.6, ease: 'easeOut' },
        },
    };

    const handleCardClick = (path, domain = '') => {
        const hostname = window.location.hostname;

        // For localhost or main domain (muthumuniayurvedaresorts.com), navigate locally
        if (hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname.includes('muthumuniayurvedaresorts.com')) {
            navigate(path);
        } else {
            // For other domains, redirect to the specific domain (shouldn't normally happen)
            window.location.href = `${domain}${path}`;
        }
    };

    return (
        <>

            <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#0a1814] via-[#0d1f1a] to-[#0a1814] text-white">
                {/* Animated background layers */}
                <motion.div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{ y: bgY }}
                >
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.14),transparent_50%)]" />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(245,158,11,0.09),transparent_60%)]" />
                </motion.div>

                {/* Floating orbs */}
                <motion.div
                    className="absolute top-[12%] left-[8%] w-72 h-72 bg-emerald-600/10 rounded-full blur-3xl"
                    animate={{ x: [0, 60, 0], y: [0, -40, 0], scale: [1, 1.1, 1] }}
                    transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.div
                    className="absolute bottom-[15%] right-[10%] w-80 h-80 bg-amber-500/10 rounded-full blur-3xl"
                    animate={{ x: [0, -70, 0], y: [0, 50, 0], scale: [1, 1.12, 1] }}
                    transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
                />

                {/* Logo Splash */}
                <AnimatePresence>
                    {showLogo && (
                        <motion.div
                            key="splash"
                            className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#0a1814] to-[#0f221c]"
                            variants={logoVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <motion.div
                                animate={{ rotate: [0, 4, -4, 0], scale: [1, 1.04, 1] }}
                                transition={{ duration: 6, repeat: Infinity, repeatType: 'mirror' }}
                            >
                                <img
                                    src={logo}
                                    alt="Muthumuni Ayurveda"
                                    className="w-48 h-48 md:w-72 md:h-72 object-contain drop-shadow-2xl"
                                />
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Main Content */}
                <AnimatePresence>
                    {showCards && (
                        <motion.div
                            key="content"
                            className="relative z-10 min-h-screen flex flex-col items-center justify-center px-5 py-16 md:py-20"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {/* Mini brand pill */}
                            <motion.div
                                className="mb-10 md:mb-14"
                                initial={{ opacity: 0, y: -30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: 'easeOut' }}
                            >
                                <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/8 border border-white/12 backdrop-blur-xl shadow-xl">
                                    <img src={logo} alt="" className="w-7 h-7 object-contain" />
                                    <span className="text-sm tracking-[0.28em] uppercase font-light text-emerald-100/90">
                                        Muthumuni Ayurveda
                                    </span>
                                </div>
                            </motion.div>

                            {/* Hero text */}
                            <motion.div
                                className="text-center max-w-4xl mb-16 md:mb-20"
                                initial={{ opacity: 0, y: -40 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3, duration: 1 }}
                            >
                                <div className="inline-block mb-5 px-5 py-1.5 rounded-full bg-emerald-950/40 border border-emerald-400/20 text-emerald-200/90 text-sm uppercase tracking-wider">
                                    Timeless Healing • Sri Lankan Soul
                                </div>

                                <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif tracking-tight leading-tight mb-6 md:mb-8">
                                    Muthumuni Ayurveda Resorts
                                </h1>

                                <p className="text-lg md:text-xl text-emerald-100/80 font-light max-w-3xl mx-auto leading-relaxed">
                                    Two sacred sanctuaries. One timeless philosophy.
                                    Choose your path to restoration, balance, and deep inner peace.
                                </p>
                            </motion.div>

                            {/* Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-9 lg:gap-12 w-full max-w-7xl">
                                {resorts.map((resort) => (
                                    <motion.div
                                        key={resort.id}
                                        variants={cardVariants}
                                        whileHover="hover"
                                        onClick={() => handleCardClick(resort.path, resort.domain)}
                                        className="group cursor-pointer"
                                    >
                                        <div className="relative rounded-3xl overflow-hidden h-[460px] md:h-[580px] lg:h-[640px] shadow-2xl shadow-black/50 border border-white/8 bg-black/30 backdrop-blur-sm transition-all duration-500 group-hover:border-emerald-400/30">
                                            {/* Image + zoom */}
                                            <motion.img
                                                src={resort.image}
                                                alt={resort.name}
                                                className="absolute inset-0 w-full h-full object-cover brightness-[0.82] transition-all duration-700 group-hover:brightness-[0.95]"
                                                variants={imageVariants}
                                                whileHover="hover"
                                            />

                                            {/* Gradient overlays */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
                                            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent opacity-80" />

                                            {/* Tag */}
                                            <div className="absolute top-6 left-6 z-10">
                                                <span className="px-4 py-1.5 bg-black/40 backdrop-blur-md border border-white/10 rounded-full text-sm font-medium text-emerald-100">
                                                    {resort.tag}
                                                </span>
                                            </div>

                                            {/* Content glass card */}
                                            <div className="absolute inset-x-5 bottom-5 md:inset-x-7 md:bottom-7 z-10">
                                                <div className="bg-black/45 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 shadow-2xl transition-all duration-400 group-hover:bg-black/55 group-hover:border-emerald-400/20">
                                                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-medium leading-tight mb-3 whitespace-pre-line text-white">
                                                        {resort.name}
                                                    </h2>

                                                    <p className="text-base md:text-lg text-emerald-100/85 mb-5 md:mb-6 font-light">
                                                        {resort.description}
                                                    </p>

                                                    <div className="flex flex-wrap gap-2.5 mb-6">
                                                        {(resort.highlights || []).map((highlight) => (
                                                            <span
                                                                key={highlight}
                                                                className="px-3.5 py-1 bg-emerald-950/50 border border-emerald-400/20 rounded-full text-xs md:text-sm text-emerald-100/90"
                                                            >
                                                                {highlight}
                                                            </span>
                                                        ))}
                                                    </div>

                                                    <motion.button
                                                        className="px-7 py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-semibold rounded-full text-base md:text-lg shadow-lg shadow-amber-900/30 transition-all duration-300 flex items-center gap-2 group-hover:shadow-xl group-hover:shadow-amber-900/40"
                                                        whileHover={{ scale: 1.05, x: 4 }}
                                                        whileTap={{ scale: 0.97 }}
                                                    >
                                                        {resort.cta}
                                                        <span className="text-xl">→</span>
                                                    </motion.button>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Subtle footer decoration */}
                            <motion.div
                                className="mt-16 md:mt-24 flex gap-3"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 1.2, duration: 1 }}
                            >
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <motion.div
                                        key={i}
                                        className="w-2.5 h-2.5 rounded-full bg-amber-500/60"
                                        animate={{
                                            scale: [1, 1.6, 1],
                                            opacity: [0.4, 0.9, 0.4],
                                        }}
                                        transition={{
                                            duration: 3.2,
                                            repeat: Infinity,
                                            delay: i * 0.4,
                                            ease: 'easeInOut',
                                        }}
                                    />
                                ))}
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

        </>
    );
};

export default Open;